import { Vector as VectorSource, Cluster as ClusterSource } from "ol/source";
import { Layer, Vector as VectorLayer } from "ol/layer";
import { getMap } from "./GeoLiteMap";
import { getAllFeatures } from "./rest";
import { getClusterSymbol, getSimpleSymbol } from "./Symbols/clusterSymbols";
import { Feature } from "ol";
import { IFeatureLayerData } from "./interfaces";
import { MultiPolygon, Polygon } from "ol/geom";

const zIndex = 1000;

export const createClusters = (map, configA, clusterSource: ClusterSource): Layer[] => {
    const clusterLayers = configA.layers.filter((l) => l.type === "ClusterLayer");
    if (!clusterLayers?.length) {
        return;
    }
    const clusterSharerLayerId = clusterLayers.find((cl) => cl.clusterSharingLayer)?.clusterSharingLayer;
    const sharingLayerConfig = clusterLayers.find((cl) => cl.id === clusterSharerLayerId);
    if (!sharingLayerConfig) {
        return;
    }

    clusterSource.set("sharingLayerConfig", sharingLayerConfig);

    addClusterFeatures(map, clusterLayers, clusterSource);
};

const addClusterFeatures = async (map, clusterLayers, clusterSource) => {
    const allClusterFeatures = await Promise.all([
        ...clusterLayers.reverse().map(async (l, i) => {
            const clusterMinScaleThreshold = l.cluster?.clusterMinScale || 0;
            const url = l.layerId !== undefined ? l.url + "/" + l.layerId : l.url;

            const mapLayer = map
                .getLayers()
                .getArray()
                .find((ml) => ml.get("id") === l.id);

            const features = await getAllFeatures(url);

            if (features.length) {
                const layerData: IFeatureLayerData = {
                    id: l.id,
                    layerId: l.layerId,
                    cluster:
                        l.cluster ||
                        clusterLayers.find((configLayer) => configLayer.id === l.clusterSharingLayer).cluster,
                    vectorStyles: l.vectorStyles,
                    identify: l.identify
                };
                addLayerData(features, layerData);
            }
            if (mapLayer) {
                (mapLayer as VectorLayer<VectorSource>).setSource(clusterSource);
                mapLayer.on("change:visible", (e) => onChangeVisibility(e, features, clusterSource));
            }
            return mapLayer.getVisible() ? features : [];
        })
    ]);

    clusterSource.getSource().addFeatures(allClusterFeatures.flat());

    addClusterLayer(clusterSource);
};

const onChangeVisibility = (e, features: Feature[], clusterSource: ClusterSource) => {
    if (e.target.getVisible()) {
        clusterSource.getSource().addFeatures(features);
        return;
    }
    const restFeatures = clusterSource
        .getSource()
        .getFeatures()
        .filter((clf) => !features.find((f) => f.get("OBJECTID") === clf.get("OBJECTID")));
    clusterSource.getSource().clear();
    clusterSource.getSource().addFeatures(restFeatures);
};

const addClusterLayer = (clusterSource) => {
    const sharingLayerConfig = clusterSource.get("sharingLayerConfig");
    const vectorStyles = sharingLayerConfig.vectorStyles;
    const clusterMinScaleThreshold = sharingLayerConfig.cluster.clusterMinScale || 0;
    clusterSource.setDistance(sharingLayerConfig.cluster.distance || 200);
    clusterSource.setMinDistance(sharingLayerConfig.cluster.minDistance || 0);
    const olLayer = new VectorLayer({
        properties: { id: "mainCluster" },
        source: clusterSource,
        // minResolution: l.minResolution,
        // maxResolution: l.maxResolution,
        zIndex: 5000,
        opacity: 1,
        style: (clusterFeature) => {
            const clusterFeatures = clusterFeature.get("features");
            return clusterFeatures?.length > 1
                ? getClusterSymbol(clusterFeature.get("HOVERED"), clusterFeatures.length, sharingLayerConfig.cluster)
                : getSimpleSymbol(clusterFeature, vectorStyles);
        }
    });
    document.addEventListener("moveend", (e: CustomEvent) => {
        clusterSource.setDistance(
            e.detail <= clusterMinScaleThreshold ? 0.00001 : sharingLayerConfig.cluster.distance || 200
        );
        vectorStyles.labels && olLayer.changed();
    });

    getMap().addLayer(olLayer);
};

export const addLayerData = (features: Feature[], layerData: IFeatureLayerData) => {
    for (const feature of features) {
        feature.set("layerData", layerData, true);
    }
};

export const createClusterSource = (): ClusterSource =>
    new ClusterSource({
        source: new VectorSource(),
        geometryFunction: (feature: any) => {
            const geometry = feature.getGeometry();
            const type = geometry.getType();
            return type === "Polygon"
                ? (geometry as Polygon).getInteriorPoint()
                : type === "MultiPolygon"
                ? (geometry as MultiPolygon).getInteriorPoints().getPoint(1)
                : geometry;
        }
    });
